import type { StrategyInfo, StrategyListItemV2 } from '../types/strategy';
import { getExchangeName, getQuoteCurrency } from '../types/opportunity';

export interface BuyOpenSpendInput {
  currentState: string;
  exchange: string | null;
  quoteAsset: string | null;
  budgetUsdt: number;
  spentQuote: number;
}

export interface RequiredSpendAmount {
  amount: number;
  quote: 'KRW' | 'USDT';
}

export interface StrategyBudgetGuardrailInput {
  availableBalanceQuote: number;
  reservedSpendQuote: number;
  requestedBudgetUsdt: number;
  quoteAsset: 'KRW' | 'USDT';
  usdtKrwRate: number | null;
}

export interface StrategyBudgetGuardrail {
  availableBalanceQuote: number;
  reservedSpendQuote: number;
  requestedSpendQuote: number;
  totalRequiredQuote: number;
  shortfallQuote: number;
  quoteAsset: 'KRW' | 'USDT';
  blocked: boolean;
}

function toDisplayExchangeName(
  exchangeName: string | null | undefined,
): string | null {
  if (!exchangeName) return null;
  if (exchangeName.endsWith('Futures')) return exchangeName;

  switch (exchangeName.toLowerCase()) {
    case 'binance':
      return 'Binance';
    case 'upbit':
      return 'Upbit';
    case 'bithumb':
      return 'Bithumb';
    default:
      return exchangeName;
  }
}

export function buildV2BuyOpenSpendInputs(
  strategies: StrategyListItemV2[],
): BuyOpenSpendInput[] {
  return strategies.map((strategy) => ({
    currentState: strategy.current_state,
    exchange: toDisplayExchangeName(strategy.spot_exchange_name),
    quoteAsset: strategy.spot_quote_asset ?? strategy.buy_quote,
    budgetUsdt: strategy.budget_usdt,
    spentQuote: strategy.spot_net_quote_exposure,
  }));
}

export function buildV1BuyOpenSpendInputs(
  strategies: StrategyInfo[],
): BuyOpenSpendInput[] {
  return strategies.flatMap((strategy) => {
    const opportunity = strategy.execution_context.opportunity;
    if (!opportunity) return [];

    const exchange = getExchangeName(opportunity.buy_exchange);
    const quoteAsset = getQuoteCurrency(opportunity.buy_quote);
    const spentQuote = Object.values(
      strategy.execution_context.orders ?? {},
    ).reduce((total, order) => {
      const orderExchange = toDisplayExchangeName(order.exchange);
      if (
        orderExchange !== exchange ||
        order.quote.toUpperCase() !== quoteAsset
      ) {
        return total;
      }

      const orderTotal =
        order.total ?? order.executed_volume * order.price;
      if (orderTotal <= 0) return total;
      return total + (order.side === 'Bid' ? orderTotal : -orderTotal);
    }, 0);

    return [{
      currentState: strategy.current_state,
      exchange,
      quoteAsset,
      budgetUsdt: opportunity.max_profitable_volume ?? 0,
      spentQuote,
    }];
  });
}

export function calculateStrategyBudgetGuardrail({
  availableBalanceQuote,
  reservedSpendQuote,
  requestedBudgetUsdt,
  quoteAsset,
  usdtKrwRate,
}: StrategyBudgetGuardrailInput): StrategyBudgetGuardrail | null {
  if (
    !Number.isFinite(availableBalanceQuote) ||
    availableBalanceQuote < 0 ||
    !Number.isFinite(reservedSpendQuote) ||
    reservedSpendQuote < 0 ||
    !Number.isFinite(requestedBudgetUsdt) ||
    requestedBudgetUsdt < 0
  ) {
    return null;
  }

  let requestedSpendQuote = requestedBudgetUsdt;
  if (quoteAsset === 'KRW') {
    if (!usdtKrwRate || !Number.isFinite(usdtKrwRate) || usdtKrwRate <= 0) {
      return null;
    }
    requestedSpendQuote *= usdtKrwRate;
  }

  const totalRequiredQuote = reservedSpendQuote + requestedSpendQuote;
  const shortfallQuote = Math.max(
    totalRequiredQuote - availableBalanceQuote,
    0,
  );

  return {
    availableBalanceQuote,
    reservedSpendQuote,
    requestedSpendQuote,
    totalRequiredQuote,
    shortfallQuote,
    quoteAsset,
    blocked: shortfallQuote > Number.EPSILON,
  };
}

export function calculateRequiredSpendByExchange(
  inputs: BuyOpenSpendInput[],
  usdtKrwRate: number | null,
): Record<string, RequiredSpendAmount> {
  const requiredByExchange: Record<string, RequiredSpendAmount> = {};

  for (const input of inputs) {
    if (
      input.currentState !== 'BuyOpen' ||
      !input.exchange ||
      !Number.isFinite(input.budgetUsdt) ||
      input.budgetUsdt <= 0 ||
      !Number.isFinite(input.spentQuote)
    ) {
      continue;
    }

    const quoteAsset = input.quoteAsset?.toUpperCase();
    let budgetInQuote: number;

    if (quoteAsset === 'KRW') {
      if (!usdtKrwRate || !Number.isFinite(usdtKrwRate) || usdtKrwRate <= 0) {
        continue;
      }
      budgetInQuote = input.budgetUsdt * usdtKrwRate;
    } else if (quoteAsset === 'USDT') {
      budgetInQuote = input.budgetUsdt;
    } else {
      continue;
    }

    const remaining = Math.max(budgetInQuote - input.spentQuote, 0);
    if (remaining <= 0) continue;

    const existing = requiredByExchange[input.exchange];
    if (existing && existing.quote === quoteAsset) {
      existing.amount += remaining;
    } else {
      requiredByExchange[input.exchange] = {
        amount: remaining,
        quote: quoteAsset,
      };
    }
  }

  return requiredByExchange;
}
